import type { ReactNode } from "react";
import { MoreHorizontal } from "lucide-react";

/**
 * The overflow menu for a row's less-common actions.
 *
 * A `<details>` rather than a popover library — one fewer dependency, keyboard operable for free,
 * and it is the pattern the builder canvas already uses.
 *
 * What belongs in here is the judgement that matters: rarely-used and destructive actions, so the
 * row itself shows only what someone reaches for daily. The forms list previously laid out Open,
 * Copy link, Submissions, Analytics, Archive and Delete as six identical buttons, which made
 * Delete exactly as prominent as Copy link.
 */
export const ActionMenu = ({
  label,
  children,
  align = "right",
}: {
  /** Accessible name — "Actions for the 2026 Scholarship form", not "Actions". */
  label: string;
  children: ReactNode;
  align?: "left" | "right";
}): JSX.Element => (
  <details className="relative shrink-0" onClick={(event) => event.stopPropagation()}>
    <summary
      aria-label={label}
      className="flex cursor-pointer list-none items-center rounded-md px-1.5 py-1 text-muted-foreground hover:bg-muted hover:text-foreground"
    >
      <MoreHorizontal aria-hidden className="h-4 w-4" />
    </summary>
    <div
      className={`absolute z-20 mt-1 w-56 space-y-0.5 rounded-md border bg-card p-1 shadow-lg ${
        align === "right" ? "right-0" : "left-0"
      }`}
    >
      {children}
    </div>
  </details>
);

/** One row inside an ActionMenu. `tone="danger"` for anything that cannot be undone. */
export const ActionMenuItem = ({
  onClick,
  children,
  tone = "default",
  disabled,
  title,
}: {
  onClick: () => void;
  children: ReactNode;
  tone?: "default" | "danger";
  disabled?: boolean;
  title?: string;
}): JSX.Element => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs disabled:opacity-50 ${
      tone === "danger" ? "text-destructive hover:bg-destructive/10" : "hover:bg-muted"
    }`}
  >
    {children}
  </button>
);
